import * as ex from 'excalibur';
import * as Tone from 'tone';

/**
 * AudioManager handles all sound effects and procedural audio for the game using Tone.js
 */
export class AudioManager {
  private static instance: AudioManager;
  private initialized = false;
  private volume = 0.5; // Overall volume
  private engine: ex.Engine | null = null;

  // Tone.js instruments and effects
  private pluckSynth: Tone.PluckSynth | null = null;
  private panner: Tone.Panner | null = null;
  private gainNode: Tone.Gain | null = null;
  private lastTriggerTime: number = 0;
  private minTimeBetweenSounds: number = 0.1; // Minimum time in seconds between sounds

  // Ambient sound system
  private windSynth: Tone.Synth | null = null;
  private windNoise: Tone.Noise | null = null;
  private windFilter: Tone.Filter | null = null;
  private windGain: Tone.Gain | null = null;
  private ambientDrone: Tone.Synth | null = null;
  private rainNoise: Tone.Noise | null = null;
  private rainFilter: Tone.Filter | null = null;
  private rainGain: Tone.Gain | null = null;
  private snowNoise: Tone.Noise | null = null;
  private snowFilter: Tone.Filter | null = null;
  private snowGain: Tone.Gain | null = null;

  // Ambient state
  private weatherType: 'none' | 'rain' | 'snow' = 'none';
  private windIntensity: number = 0;
  private droneEnabled: boolean = false;

  private constructor() {
    // Initialize on first sound play to avoid autoplay restrictions
  }

  /**
   * Get the singleton instance of AudioManager
   */
  public static getInstance(): AudioManager {
    if (!AudioManager.instance) {
      AudioManager.instance = new AudioManager();
    }
    return AudioManager.instance;
  }

  /**
   * Initialize the audio system and Tone.js
   */
  public async initialize(engine?: ex.Engine): Promise<void> {
    if (this.initialized) return;

    console.log("Initializing AudioManager with Tone.js");

    if (engine) {
      this.engine = engine;
    }

    try {
      // Start Tone.js context
      await Tone.start();
      console.log("Tone.js started");

      // Create a pluck synth for our hop sound
      this.pluckSynth = new Tone.PluckSynth({
        attackNoise: 1,         // Lower value makes a sharper attack
        dampening: 2000,        // Lower value makes it brighter
        resonance: 0.9,         // Higher value makes it ring longer
        release: 0.4            // Slightly longer release for more sustain
      });

      // Create panner for spatial audio
      this.panner = new Tone.Panner(0).toDestination();

      // Create master volume control
      this.gainNode = new Tone.Gain(this.volume);

      // Connect the signal chain
      this.pluckSynth.connect(this.gainNode);
      this.gainNode.connect(this.panner);

      // Initialize ambient sound system
      this.initializeAmbientSounds();

      this.initialized = true;
      console.log("AudioManager initialized successfully");
    } catch (error) {
      console.error("Failed to initialize AudioManager:", error);
    }
  }

  /**
   * Initialize the ambient sound generators
   */
  private initializeAmbientSounds(): void {
    try {
      // Start Tone.js Transport
      if (Tone.Transport.state !== "started") {
        Tone.Transport.start();
      }

      // Wind generator
      this.windNoise = new Tone.Noise("white");
      this.windFilter = new Tone.Filter({
        type: "highpass",  // Changed to highpass for more realistic wind
        frequency: 800,
        Q: 0.5
      });
      this.windGain = new Tone.Gain(0).toDestination();

      // Connect wind chain
      this.windNoise.connect(this.windFilter);
      this.windFilter.connect(this.windGain);

      // Ambient drone (low pad sound)
      this.ambientDrone = new Tone.Synth({
        oscillator: {
          type: 'sine'
        },
        envelope: {
          attack: 3,
          decay: 0.2,
          sustain: 1.0,
          release: 5
        }
      });
      const droneGain = new Tone.Gain(0.15).toDestination();
      this.ambientDrone.connect(droneGain);

      // Rain generator - use white noise for more pronounced rain
      this.rainNoise = new Tone.Noise("white");
      this.rainFilter = new Tone.Filter({
        type: "bandpass",
        frequency: 3000,
        Q: 0.3
      });
      this.rainGain = new Tone.Gain(0).toDestination();

      // Connect rain chain
      this.rainNoise.connect(this.rainFilter);
      this.rainFilter.connect(this.rainGain);

      // Snow generator (very subtle noise)
      this.snowNoise = new Tone.Noise("pink");
      this.snowFilter = new Tone.Filter({
        type: "highpass",
        frequency: 5000,
        Q: 0.8
      });
      this.snowGain = new Tone.Gain(0).toDestination();

      // Connect snow chain
      this.snowNoise.connect(this.snowFilter);
      this.snowFilter.connect(this.snowGain);

      console.log("Ambient sound system initialized");
    } catch (error) {
      console.error("Failed to initialize ambient sounds:", error);
    }
  }

  /**
   * Set master volume
   */
  public setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
    if (this.gainNode) {
      this.gainNode.gain.value = this.volume;
    }
  }

  /**
   * Play a hop sound for animal movement, with spatial audio
   * @param worldX X position in world coordinates
   * @param worldY Y position in world coordinates
   */
  public async playHopSound(worldX?: number, worldY?: number): Promise<void> {
    await this.ensureInitialized();

    // Skip if volume is muted
    if (this.volume <= 0) return;

    try {
      // Calculate pan based on position relative to viewport
      let pan = 0;

      // Log the coordinates for debugging
      console.log(`Playing hop sound at world coordinates: (${worldX}, ${worldY})`);

      if (worldX !== undefined && worldY !== undefined && this.engine) {
        // Convert world position to screen position using the engine method
        const screenPos = this.engine.worldToScreenCoordinates(new ex.Vector(worldX, worldY));

        // Calculate pan value (-1 to 1) based on horizontal position in viewport
        // -1 is full left, 1 is full right
        const viewportWidth = this.engine.screen.viewport.width;
        // Calculate and clamp to valid range [-1, 1]
        pan = Math.max(-1, Math.min(1, (screenPos.x / viewportWidth) * 2 - 1));

        // Calculate volume adjustment based on distance from center of viewport
        // Further from center = quieter
        const viewportCenter = new ex.Vector(
          viewportWidth / 2,
          this.engine.screen.viewport.height / 2
        );

        // Calculate distance from screen center
        const distanceFromCenter = Math.sqrt(
          Math.pow(screenPos.x - viewportCenter.x, 2) +
          Math.pow(screenPos.y - viewportCenter.y, 2)
        );
        const maxDistance = Math.sqrt(
          Math.pow(viewportWidth / 2, 2) +
          Math.pow(this.engine.screen.viewport.height / 2, 2)
        );

        // Reduce volume for distant sounds but keep them audible
        // Calculate and clamp between 0.5 and 1
        const distanceVolume = Math.max(0.5, Math.min(1, 1 - (distanceFromCenter / maxDistance) * 0.5));

        // Apply panning
        if (this.panner) {
          this.panner.pan.value = pan;
        }

        // Adjust volume based on distance (clamp between 0 and 1)
        if (this.gainNode) {
          this.gainNode.gain.value = Math.max(0, Math.min(1, this.volume * distanceVolume));
        }
      }

      // Get current Tone.js time
      const now = Tone.now();

      // Check if we need to wait before playing another sound
      // This prevents the "Start time must be strictly greater than previous start time" error
      if (now - this.lastTriggerTime < this.minTimeBetweenSounds) {
        console.log(`Skipping sound, too soon after last trigger (${now - this.lastTriggerTime}s)`);
        return;
      }

      // Play a slightly random note for natural variation
      // Using a pentatonic scale for pleasant-sounding notes
      const notes = ['C4', 'D4', 'E4', 'G4', 'A4', 'C5', 'D5', 'E5'];
      const noteIdx = Math.floor(Math.random() * notes.length);
      const note = notes[noteIdx];

      // Trigger the pluck sound with a new approach
      if (this.pluckSynth) {
        try {
          // Create a slight delay to ensure timing
          const triggerTime = now + 0.01;
          this.pluckSynth.triggerAttack(note, triggerTime);
          this.lastTriggerTime = triggerTime;
          console.log(`Triggered sound at time ${triggerTime}, note: ${note}`);
        } catch (soundError) {
          console.error("Error triggering pluck sound:", soundError);
        }
      }
    } catch (error) {
      console.error("Failed to play hop sound:", error);
    }
  }

  /**
   * Start wind ambient sound
   * @param intensity From 0.0 to 1.0
   */
  public startWind(intensity: number = 0.5): void {
    if (!this.initialized || this.volume <= 0) return;

    try {
      intensity = Math.max(0, Math.min(1, intensity));
      this.windIntensity = intensity;

      if (this.windNoise && this.windFilter && this.windGain) {
        // Start Tone.js Transport if not already started
        if (Tone.Transport.state !== "started") {
          Tone.Transport.start();
        }

        // Make filter much higher to avoid sounding like rain
        // Wind should be more of a "whoosh" not a rumble
        const baseFreq = 800 + (intensity * 1200);
        this.windFilter.frequency.value = baseFreq;

        // Lower Q value for smoother sound
        this.windFilter.Q.value = 0.5;

        // Lower the gain to make it more subtle
        this.windGain.gain.value = intensity * 0.15 * this.volume;

        // Start the noise if not already started
        if (this.windNoise.state !== "started") {
          this.windNoise.start();
        }

        // Use LFO modulation for wind variation
        const lfoFreq = 0.1 + (intensity * 0.3); // between 0.1 and 0.4 Hz
        const lfoDepth = 100 + (intensity * 200); // between 100 and 300 Hz

        // Clear previous schedulings
        Tone.Transport.cancel();

        // Reset filter frequency with subtle modulation over time
        // This creates a more natural wind sound that ebbs and flows
        Tone.Transport.scheduleRepeat((time) => {
          // Create a slow, natural modulation of the filter
          const newFreq = baseFreq + (Math.sin(Date.now() * 0.001 * lfoFreq) * lfoDepth);
          this.windFilter?.frequency.rampTo(newFreq, 0.5);

          // Also modulate the volume slightly
          const volMod = 0.9 + (Math.sin(Date.now() * 0.0008) * 0.1);
          this.windGain?.gain.rampTo(intensity * 0.15 * this.volume * volMod, 0.8);
        }, 0.5, 0);

        console.log(`Wind started with intensity ${intensity}`);
      }
    } catch (error) {
      console.error("Failed to start wind:", error);
    }
  }

  /**
   * Stop wind ambient sound
   */
  public stopWind(): void {
    try {
      if (this.windGain) {
        // Gradually fade out the wind
        this.windGain.gain.rampTo(0, 1.0);

        // Stop the wind noise after fadeout
        setTimeout(() => {
          if (this.windNoise && this.windNoise.state === "started") {
            this.windNoise.stop();
          }
        }, 1100);
      }

      this.windIntensity = 0;
      console.log("Wind stopped");
    } catch (error) {
      console.error("Failed to stop wind:", error);
    }
  }

  /**
   * Start rain ambient sound
   * @param intensity From 0.0 to 1.0
   */
  public startRain(intensity: number = 0.7): void {
    if (!this.initialized || this.volume <= 0) return;

    try {
      // Make sure Tone.js is running
      if (Tone.Transport.state !== "started") {
        Tone.Transport.start();
      }

      // Ensure we've created the rain synth
      if (!this.rainNoise) {
        console.log("Recreating rain synth");
        this.rainNoise = new Tone.Noise("white");
        this.rainFilter = new Tone.Filter({
          type: "bandpass",
          frequency: 3000,
          Q: 0.3
        });
        this.rainGain = new Tone.Gain(0).toDestination();

        // Connect rain chain
        this.rainNoise.connect(this.rainFilter);
        this.rainFilter.connect(this.rainGain);
      }

      intensity = Math.max(0, Math.min(1, intensity));
      this.weatherType = 'rain';

      if (this.rainNoise && this.rainFilter && this.rainGain) {
        // Adjust filter frequency and Q based on intensity
        this.rainFilter.frequency.value = 2800 + (intensity * 1000);
        this.rainFilter.Q.value = 0.3 + (intensity * 0.1);

        // Set the gain based on intensity and master volume
        // Make rain more pronounced
        this.rainGain.gain.value = intensity * 0.35 * this.volume;

        // Start the noise if not already started
        if (this.rainNoise.state !== "started") {
          console.log("Starting rain noise");
          this.rainNoise.start();
        }

        // Create a modulating effect for rain patter
        // Clear previous schedulings
        Tone.Transport.cancel();

        // Schedule subtle gain modulation for more natural sound
        Tone.Transport.scheduleRepeat((time) => {
          if (this.rainGain) {
            const rainMod = 0.8 + (Math.random() * 0.4); // Random fluctuations
            this.rainGain.gain.rampTo(intensity * 0.35 * this.volume * rainMod, 0.1);
          }
        }, 0.15);

        // Also start some wind for realism
        this.startWind(intensity * 0.7);

        console.log(`Rain started with intensity ${intensity}`);
      } else {
        console.error("Rain synth not properly initialized");
      }
    } catch (error) {
      console.error("Failed to start rain:", error);
    }
  }

  /**
   * Start snow ambient sound (very subtle)
   * @param intensity From 0.0 to 1.0
   */
  public startSnow(intensity: number = 0.5): void {
    if (!this.initialized || this.volume <= 0) return;

    try {
      // Make sure Tone.js is running
      if (Tone.Transport.state !== "started") {
        Tone.Transport.start();
      }

      // Ensure we've created the snow synth
      if (!this.snowNoise) {
        console.log("Recreating snow synth");
        this.snowNoise = new Tone.Noise("pink");
        this.snowFilter = new Tone.Filter({
          type: "highpass",
          frequency: 5000,
          Q: 0.8
        });
        this.snowGain = new Tone.Gain(0).toDestination();

        // Connect snow chain
        this.snowNoise.connect(this.snowFilter);
        this.snowFilter.connect(this.snowGain);
      }

      intensity = Math.max(0, Math.min(1, intensity));
      this.weatherType = 'snow';

      if (this.snowNoise && this.snowFilter && this.snowGain) {
        // Snow sound is very subtle high-frequency noise
        this.snowFilter.frequency.value = 5000 + (intensity * 2000);
        this.snowFilter.Q.value = 0.8 - (intensity * 0.2);

        // Set the gain very low - snow is quiet
        this.snowGain.gain.value = intensity * 0.1 * this.volume;

        // Start the noise if not already started
        if (this.snowNoise.state !== "started") {
          console.log("Starting snow noise");
          this.snowNoise.start();
        }

        // Clear previous schedulings
        Tone.Transport.cancel();

        // Schedule very subtle modulation for snow
        Tone.Transport.scheduleRepeat((time) => {
          if (this.snowGain && this.snowFilter) {
            // Subtle frequency modulation
            const freqMod = 5000 + (intensity * 2000) + (Math.random() * 500);
            this.snowFilter.frequency.rampTo(freqMod, 0.3);

            // Even subtler gain modulation
            const gainMod = 0.9 + (Math.random() * 0.2);
            this.snowGain.gain.rampTo(intensity * 0.1 * this.volume * gainMod, 0.5);
          }
        }, 0.5);

        // Start light wind for realism
        this.startWind(intensity * 0.4);

        console.log(`Snow started with intensity ${intensity}`);
      } else {
        console.error("Snow synth not properly initialized");
      }
    } catch (error) {
      console.error("Failed to start snow:", error);
    }
  }

  /**
   * Stop all weather sounds
   */
  public stopWeather(): void {
    try {
      // Stop rain if active
      if (this.rainGain && this.rainNoise) {
        // Fade out
        this.rainGain.gain.rampTo(0, 0.2);

        // Stop after fadeout
        setTimeout(() => {
          if (this.rainNoise && this.rainNoise.state === "started") {
            this.rainNoise.stop();
          }
        }, 600);
      }

      // Stop snow if active
      if (this.snowGain && this.snowNoise) {
        // Fade out
        this.snowGain.gain.rampTo(0, 0.2);

        // Stop after fadeout
        setTimeout(() => {
          if (this.snowNoise && this.snowNoise.state === "started") {
            this.snowNoise.stop();
          }
        }, 600);
      }

      // Stop wind also
      this.stopWind();

      this.weatherType = 'none';
      console.log("All weather sounds stopped");
    } catch (error) {
      console.error("Failed to stop weather sounds:", error);
    }
  }

  /**
   * Start ambient drone
   */
  public startDrone(): void {
    if (!this.initialized || this.volume <= 0) return;

    try {
      if (this.ambientDrone) {
        this.droneEnabled = true;

        // Play a very low C note
        this.ambientDrone.triggerAttack('C2');

        // Schedule notes for a gentle ambient progression
        const now = Tone.now();
        this.ambientDrone.triggerAttack('C2', now);

        // Set up a repeating pattern that plays sparse ambient notes
        Tone.Transport.scheduleRepeat((time) => {
          if (!this.droneEnabled) return;

          // Randomly choose between drone notes for ambient feel
          const notes = ['C2', 'G2', 'F2', 'A#2', 'C3'];
          const randomNote = notes[Math.floor(Math.random() * notes.length)];

          // Trigger with longer attack/release for ambient pad feel
          if (this.ambientDrone) {
            try {
              this.ambientDrone.triggerAttack(randomNote, time);

              // Schedule release after a few seconds for overlapping drones
              Tone.Transport.schedule((t) => {
                this.ambientDrone?.triggerRelease(t);
              }, time + 8);
            } catch (error) {
              console.error("Error triggering drone note:", error);
            }
          }
        }, 12);

        // Start Transport if not already started
        if (Tone.Transport.state !== "started") {
          Tone.Transport.start();
        }

        console.log("Ambient drone started");
      }
    } catch (error) {
      console.error("Failed to start ambient drone:", error);
    }
  }

  /**
   * Stop ambient drone
   */
  public stopDrone(): void {
    try {
      this.droneEnabled = false;

      if (this.ambientDrone) {
        // Trigger release for a smooth fadeout
        this.ambientDrone.triggerRelease();
      }

      console.log("Ambient drone stopped");
    } catch (error) {
      console.error("Failed to stop ambient drone:", error);
    }
  }

  /**
   * Set sound state based on weather type
   * @param weatherType The type of weather to simulate
   */
  public setWeatherSound(weatherType: 'none' | 'rain' | 'snow'): void {
    if (weatherType === this.weatherType) return;

    // Stop current weather sounds
    this.stopWeather();

    // Start the new weather type
    switch (weatherType) {
      case 'rain':
        this.startRain(0.7);
        break;
      case 'snow':
        this.startSnow(0.5);
        break;
      case 'none':
      default:
        // Just light wind for no weather
        this.startWind(0.2);
        break;
    }
  }

  /**
   * Ensure the audio system is initialized
   */
  private async ensureInitialized(): Promise<void> {
    if (!this.initialized) {
      await this.initialize();

      // If still not initialized (e.g., due to user interaction required),
      // attempt to start Tone.js again with a user gesture
      if (!this.initialized) {
        try {
          await Tone.start();
          console.log("Tone.js started on user gesture");
          this.initialized = true;
        } catch (error) {
          console.warn("Could not start Tone.js:", error);
        }
      }
    }
  }
}
